import React, { InputHTMLAttributes, memo, useEffect, useRef, useState } from "react";
import styles from "./DatePicker.module.scss";
import { Calendar } from "react-date-range";
import { CalendarOrangeIcon } from "../../../../assets/icons";

type Props = {
	initialDate: Date;
	required?: boolean;
	label?: string;
	error?: string;
	onChange: (date: Date) => void;
};

const DatePicker: React.FC<Props> = memo((props: Props) => {
	const { initialDate, onChange, error, label, required } = props;

	const [showOnTop, setShowOnTop] = useState(false);
	const dateRef = useRef<HTMLButtonElement>(null);

	const [expanded, setExpanded] = useState(false);
	const [selectionDate, setSelectionDate] = useState<Date>(initialDate);

	function toggleExpanded() {
		setExpanded(!expanded);
	}

	const handleSelect = (date: Date) => {
		setSelectionDate(date);
		setExpanded(false);
		onChange(date);
	};

	function closeDatePicker(event: React.FocusEvent) {
		if (event.relatedTarget) return;

		setExpanded(false);
	}

	useEffect(() => {
		if (dateRef.current === null) return;

		if (!expanded) return;

		const dropdown = dateRef;
		if (!dropdown) return;

		const dropdownRect = dropdown.current!.getBoundingClientRect();
		const bottomOverflow = window.innerHeight - (dropdownRect.bottom + 400);
		const topOverflow = dropdownRect.top - 400;

		if (bottomOverflow < 0) {
			setShowOnTop(true);
		} else if (topOverflow < 0) {
			setShowOnTop(false);
		} else {
			setShowOnTop(false);
		}
	}, [expanded, dateRef]);

	useEffect(() => {
		setSelectionDate(initialDate);
	}, [initialDate]);

	const formatWithOrdinal = (date: Date) => {
		const formattedDate = date.toLocaleDateString("en-us", {
			month: "short",
			day: "2-digit",
			year: "numeric",
		});

		const day = date.getDate();

		// Function to get the ordinal suffix (st, nd, rd, th)
		const getOrdinalSuffix = (day: number) => {
			if (day > 3 && day < 21) return "th"; // Covers the teens (11th, 12th, 13th, etc.)
			switch (day % 10) {
				case 1:
					return "st";
				case 2:
					return "nd";
				case 3:
					return "rd";
				default:
					return "th";
			}
		};

		// Add the ordinal suffix to the day
		const dayWithOrdinal = day + getOrdinalSuffix(day);

		// Replace the day portion in the formatted string with the day with ordinal
		return formattedDate.replace(day.toString(), dayWithOrdinal);
	};

	return (
		<div className={`${styles.inputContainer} ${props.error && styles.errorInput}`} onBlur={closeDatePicker}>
			<button type="button" className={styles.innerInputContainer} onClick={toggleExpanded}>
				<div className={`${styles.inputIcon} ${styles.orangeBg}`}>
					<CalendarOrangeIcon />
				</div>
				<div className={styles.inputContent}>
					<span className={styles.inputLabel}>
						{label} {required && <div className={styles.asterix}>&nbsp;*</div>}
					</span>
					<div className={styles.valueContainer}>{selectionDate && `${formatWithOrdinal(selectionDate)}`}</div>
					{error && <div className={styles.error}>{error}</div>}
				</div>
				<CalendarOrangeIcon />
			</button>
			<div className={`${styles.datePickerContainer} ${expanded ? styles.show : ""} ${showOnTop ? styles.selectExpandedTop : ""}`}>
				<Calendar date={selectionDate} onChange={handleSelect} color="#FF7926" />
			</div>
		</div>
	);
});

export default DatePicker;
